import { useContext, useEffect, useMemo } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { Error } from "@shared/features/bots/BotForm/common/types";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { isSymbol } from "@shared/types/utils";
import { pickBy } from "lodash";
import { BotType } from "@shared/types";

const useKeywordToSymbol = () => {
  const {
    subGroup,
    keywordToSymbolMapping,
    setKeywordToSymbolMapping,
    removeError,
    addError,
    account,
    setIsAdvancedView,
    botGeneralData,
    botType,
  } = useContext(BotFormContext)!;
  const { getAccountActiveSymbols } = useSymbolsMetadata();
  const accountSymbols = isSymbol(account)
    ? undefined
    : getAccountActiveSymbols(account?.client_id);
  const { currentData: groupData } =
    sharedLib.sharedApi.common.useGetGroupDataQuery(
      subGroup ? { sub_group_id: subGroup.id } : skipToken
    );

  const errorMessage =
    botType === BotType.SIGNAL &&
    Object.keys(groupData?.keyword_to_symbols || {}).some(
      (keyword) => !keywordToSymbolMapping?.[keyword]
    )
      ? "Please select a symbol for every asset."
      : "";

  useEffect(() => {
    if (errorMessage) {
      addError(Error.KEYWORD_TO_SYMBOL);
      setIsAdvancedView(true);
    } else removeError(Error.KEYWORD_TO_SYMBOL);
  }, [errorMessage]);

  const keywordToSymbolsMapping: Record<string, string[]> = useMemo(() => {
    return Object.entries(groupData?.keyword_to_symbols || {}).reduce(
      (acc, [keyword, symbols]) => {
        acc[keyword] =
          accountSymbols?.filter((symbol) => {
            return symbols?.includes(symbol);
          }) || [];
        return acc;
      },
      {} as Record<string, string[]>
    );
  }, [groupData, accountSymbols]);

  useEffect(() => {
    const groupHasMapping = !!Object.keys(groupData?.keyword_to_symbols || {})
      .length;
    if (groupHasMapping) {
      const baseMapping =
        botGeneralData && "keyword_to_symbol_mapping" in botGeneralData
          ? botGeneralData.keyword_to_symbol_mapping
          : undefined;
      if (baseMapping) {
        setKeywordToSymbolMapping(() => {
          return pickBy(baseMapping, (symbol, keyword) =>
            keywordToSymbolsMapping[keyword]?.includes(symbol)
          );
        });
      }
    } else if (!groupHasMapping && keywordToSymbolMapping) {
      setKeywordToSymbolMapping(undefined);
    }
  }, [groupData, botGeneralData, keywordToSymbolsMapping]);

  return {
    keywordToSymbolsMapping,
    errorMessage,
  };
};

export default useKeywordToSymbol;
